import React, { useState } from 'react';
import {
    Card, CardBody, Container, Form, Row, Col,
    Label, Input, FormFeedback, Spinner
} from 'reactstrap';
import { Btn } from '../../AbstractElements';
import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import { apiPost } from '../../Utils';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

interface TimingForm {
    service_start_time: string;
    service_end_time: string;
    slot_duration_mins: string;
    max_service_hours: string;
    bystander_response_timeout_mins: string;
    bystander_planned_response_timeout_hrs: string;
    modify_before_hrs: string;
    radius: string;
}

interface Errors {
    [key: string]: string | undefined;
}

const AddTimingSettings = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState<TimingForm>({
        service_start_time: '',
        service_end_time: '',
        slot_duration_mins: '',
        max_service_hours: '',
        bystander_response_timeout_mins: '',
        bystander_planned_response_timeout_hrs: '',
        modify_before_hrs: '',
        radius: '25',
    });

    const [errors, setErrors] = useState<Errors>({});
    const [saving, setSaving] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    const validate = (): boolean => {
        const newErrors: Errors = {};
        if (!form.service_start_time) newErrors.service_start_time = 'Start time is required.';
        if (!form.service_end_time) newErrors.service_end_time = 'End time is required.';
        
        if (form.service_start_time && form.service_end_time) {
            if (form.service_end_time <= form.service_start_time) {
                newErrors.service_end_time = 'End time must be greater than start time.';
            }
        }

        if (!form.slot_duration_mins || Number(form.slot_duration_mins) <= 0) newErrors.slot_duration_mins = 'Valid slot duration is required.';
        if (!form.max_service_hours || Number(form.max_service_hours) <= 0) newErrors.max_service_hours = 'Valid max service hours is required.';
        if (!form.bystander_response_timeout_mins || Number(form.bystander_response_timeout_mins) <= 0) newErrors.bystander_response_timeout_mins = 'Valid timeout is required.';
        if (!form.bystander_planned_response_timeout_hrs || Number(form.bystander_planned_response_timeout_hrs) <= 0) newErrors.bystander_planned_response_timeout_hrs = 'Valid planned timeout is required.';
        if (!form.modify_before_hrs || Number(form.modify_before_hrs) < 0) newErrors.modify_before_hrs = 'Valid modification cutoff is required.';
        if (!form.radius || Number(form.radius) <= 0) newErrors.radius = 'Valid radius is required.';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setSaving(true);
        try {
            await apiPost('timing/store', form);
            toast.success('Timing settings saved successfully!', { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/timing_settings`);
            }, 1000);
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to save timing settings.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add Timing Settings" parent="Master Settings" title="Add Timing Settings" />
            <Container fluid>
                <Card className="shadow-sm">
                    <CardBody>
                        <Form onSubmit={handleSubmit} noValidate>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Label className="fw-semibold">Service Start Time <span className="text-danger">*</span></Label>
                                    <Input type="time" name="service_start_time" value={form.service_start_time} onChange={handleChange} invalid={!!errors.service_start_time} />
                                    <FormFeedback>{errors.service_start_time}</FormFeedback>
                                </Col>
                                <Col md={6}>
                                    <Label className="fw-semibold">Service End Time <span className="text-danger">*</span></Label>
                                    <Input type="time" name="service_end_time" value={form.service_end_time} onChange={handleChange} invalid={!!errors.service_end_time} />
                                    <FormFeedback>{errors.service_end_time}</FormFeedback>
                                </Col>
                                <Col md={6}>
                                    <Label className="fw-semibold">Slot Duration (Mins) <span className="text-danger">*</span></Label>
                                    <Input type="number" name="slot_duration_mins" value={form.slot_duration_mins} onChange={handleChange} placeholder="Enter Slot Duration" invalid={!!errors.slot_duration_mins} />
                                    <FormFeedback>{errors.slot_duration_mins}</FormFeedback>
                                </Col>
                                <Col md={6}>
                                    <Label className="fw-semibold">Max Service Hours <span className="text-danger">*</span></Label>
                                    <Input type="number" name="max_service_hours" value={form.max_service_hours} onChange={handleChange} placeholder="Enter Max Service Hours" invalid={!!errors.max_service_hours} />
                                    <FormFeedback>{errors.max_service_hours}</FormFeedback>
                                </Col>
                                <Col md={6}>
                                    <Label className="fw-semibold">Bystander Timeout (Mins) <span className="text-danger">*</span></Label>
                                    <Input type="number" name="bystander_response_timeout_mins" value={form.bystander_response_timeout_mins} onChange={handleChange} placeholder="Enter Bystander Timeout" invalid={!!errors.bystander_response_timeout_mins} />
                                    <FormFeedback>{errors.bystander_response_timeout_mins}</FormFeedback>
                                </Col>
                                <Col md={6}>
                                    <Label className="fw-semibold">Bystander Planned Timeout (Hrs) <span className="text-danger">*</span></Label>
                                    <Input type="number" name="bystander_planned_response_timeout_hrs" value={form.bystander_planned_response_timeout_hrs} onChange={handleChange} placeholder="Enter Planned Timeout" invalid={!!errors.bystander_planned_response_timeout_hrs} />
                                    <FormFeedback>{errors.bystander_planned_response_timeout_hrs}</FormFeedback>
                                </Col>
                                <Col md={6}>
                                    <Label className="fw-semibold">Modify Before (Hrs) <span className="text-danger">*</span></Label>
                                    <Input type="number" name="modify_before_hrs" value={form.modify_before_hrs} onChange={handleChange} placeholder="Enter Hours Before Start to Disable Modify" invalid={!!errors.modify_before_hrs} />
                                    <FormFeedback>{errors.modify_before_hrs}</FormFeedback>
                                </Col>
                                <Col md={6}>
                                    <Label className="fw-semibold">Service Search Radius (km) <span className="text-danger">*</span></Label>
                                    <Input type="number" name="radius" value={form.radius} onChange={handleChange} placeholder="Enter Search Radius in KM" invalid={!!errors.radius} />
                                    <FormFeedback>{errors.radius}</FormFeedback>
                                </Col>
                            </Row>
                            <div className="mt-4 d-flex justify-content-start">
                                <Btn color="primary" type="submit" disabled={saving}>{saving ? <> Submitting <Spinner size="sm" /></> : 'Submit'}</Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
};

export default AddTimingSettings;
